import { getDefaultClient } from '../../../client/defaultClient';
import { assertWalletProviderMethodDefined } from '../utils/assertWalletProviderMethodDefined';
import { getWalletProviderFromWalletAccount } from '../utils/getWalletProviderFromWalletAccount';
import type { WalletAccount } from '../walletAccount';

type SignMessageParams = {
  message: string;
  walletAccount: WalletAccount;
};

/**
 * Signs a message using the specified wallet account.
 *
 * This function uses the wallet's private key to cryptographically sign
 * the provided message.
 *
 * @param params.walletAccount - The wallet account to use for signing.
 * @param params.message - The message to sign.
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns A promise that resolves to an object containing the signature.
 */
export const signMessage = async (
  { walletAccount, message }: SignMessageParams,
  client = getDefaultClient()
): Promise<{ signature: string }> => {
  const walletProvider = getWalletProviderFromWalletAccount(
    {
      walletAccount,
    },
    client
  );

  assertWalletProviderMethodDefined(walletProvider, 'signMessage');

  const { signature } = await walletProvider.signMessage({
    message,
    walletAccount,
  });

  return { signature };
};
